import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import useOnceEffect from "@/mixins/useOnceEffect";
import locale from "@/mixins/locale";

const localeArr = "modules.components.ImageTaker.ImageEditor";

const minSwipeDistance = 50;

let touchStart = null;
let touchEnd = null;
let swipeOverlay = null;

const ImageEditor = props => {
	const [userImg] = useState(props.userImg.img);
	const [overlayIdx, setOverlayIdx] = useState(props.userImg.overlayIdx || 0);
	const [imgMerged, setImgMerged] = useState(null);
	const [errorMessage, setErrorMessage] = useState(null);

	useOnceEffect(
		useCallback(() => {
			swipeOverlay = document.querySelector("#swipeOverlay");
			if (props.content.isGif) {
				document.querySelector("#gif-holder").src = props.userImg.img;
				return;
			}
			if (props.content.showOverlays) {
				document.querySelector("#canvas").addEventListener("touchmove", onTouchMove, {
					passive: false
				});
			}
			mergeImages();
		}, [])
	);

	const setTouchEnd = e => {
		touchEnd = e.targetTouches[0].clientX;
	};

	const onTouchStart = e => {
		setTouchEnd(e);
		touchStart = e.targetTouches[0].clientX;
	};

	const onTouchMove = e => {
		if (props.content.isGif) {
			return;
		}

		setTouchEnd(e);
		if (!touchEnd) {
			return;
		}

		let distance = (touchStart - touchEnd) / 4;
		if (Math.abs(distance) > 1) {
			e.preventDefault();
		}

		if (Math.abs(distance) < 10) {
			return;
		}

		if (distance > 0) {
			swipeOverlay.classList.add("right-swipe");
			swipeOverlay.classList.remove("left-swipe");
			distance = 60 - distance;
			if (distance > 59 || distance < 1) {
				return;
			}
		} else {
			swipeOverlay.classList.remove("right-swipe");
			swipeOverlay.classList.add("left-swipe");
			distance = 60 - Math.abs(distance);
			distance = distance * -1;
			if (distance > -1 || distance < -59) {
				return;
			}
		}

		swipeOverlay.style.transform = `translateX(${distance}px)`;
	};

	const onTouchEnd = () => {
		if (props.content.isGif) {
			return;
		}

		swipeOverlay.classList.remove("right-swipe");
		swipeOverlay.classList.add("left-swipe");
		swipeOverlay.style.transform = `translateX(-1000px)`;

		if (!touchStart || !touchEnd) return;

		const distance = touchStart - touchEnd;
		const isRightSwipe = distance > minSwipeDistance;
		const isLeftSwipe = distance < -minSwipeDistance;

		if (isRightSwipe) {
			setOverlay(1);
		}
		if (isLeftSwipe) {
			setOverlay(-1);
		}
	};

	const loadImage = async src => {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.onload = () => resolve(img);
			img.onerror = () => reject();
			img.src = src;
		});
	};

	const mergeImages = async (idx = null) => {
		const index = idx === null ?  overlayIdx : idx;
		try {
			const overlay = props.content.showOverlays ? await loadImage(props.content.overlays[index].overlay) : null;
			const img = await loadImage(userImg);
			const canvas = document.querySelector("#canvas");
			const context = canvas.getContext("2d");

			canvas.width = img.width;
			canvas.height = img.height;

			context.globalAlpha = 1.0;
			context.drawImage(img, 0, 0, img.width, img.height);
			if (overlay) {
				context.globalAlpha = 1.0;
				context.drawImage(overlay, 0, 0, img.width, img.height);
			}

			setImgMerged(canvas.toDataURL("image/jpeg"));
		} catch (e) {
			console.error(e);
			setErrorMessage(locale(localeArr + ".error"));
		}
	};

	const getOverlayPosition = position => {
		try {
			const currentIdx = overlayIdx;
			if (position === 0) {
				return currentIdx;
			}

			const ceil = props.content.overlays.length;
			let newPos = currentIdx + position;

			if (newPos >= ceil) {
				const pos = newPos - ceil;
				if (pos >= ceil) {
					return ceil - 1;
				}
				return pos;
			}
			if (newPos < 0) {
				return ceil + newPos;
			}

			return newPos;
		} catch (e) {
			console.error(e);
			return 0;
		}
	};

	const thumbToShow = position => {
		let newPos = getOverlayPosition(position);
		if (newPos < 0) {
			newPos = 0;
		}

		return props.content.overlays[newPos].thumb;
	};

	const setOverlay = position => {
		let idx = getOverlayPosition(position);

		setOverlayIdx(idx);
		mergeImages(idx);
	};

	const setUserImage = () => {
		const imageObj = {
			img: userImg,
			imgMerged: imgMerged || userImg,
			overlayIdx: overlayIdx
		};

		props.setUserImage(imageObj);
	};

	const downloadImage = () => {
		const link = document.createElement("a");
		const ext = props.content.isGif ? "gif" : "png";
		const url = props.content.isGif ? userImg : document.getElementById("canvas").toDataURL();

		link.download = "file." + ext;
		link.href = url;
		link.click();
	};

	return (
		<div className="image-editor flex flex-col">
			<div className="canvas-holder relative overflow-hidden mt-0">
				{props.content.isGif && <img src="" id="gif-holder" />}
				{!props.content.isGif && <canvas id="canvas" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} className="max-w-full w-full" />}
				<div id="swipeOverlay"></div>
			</div>

			<div className={`container ${errorMessage ? "pt-3 pb-0" : "py-3"}`}>
				{props.content.overlays && !props.content.isGif && (
					<div className="row-span-full py-1 flex justify-center">
						{props.content?.showOverlays &&
							[-2, -1, 0, 1, 2].map(item => {
								return (
									<div key={"thumb-slider-" + item} className="col-auto flex items-center thumb-holder-parent">
										<div
											onClick={() => setOverlay(item)}
											className={`thumb-holder flex items-center justify-center relative overflow-hidden ${
												item === 0 ? "current-thumb" : null
											}`}>
											<img src={thumbToShow(item)} />
										</div>
									</div>
								);
							})}
					</div>
				)}

				{errorMessage && (
					<>
						<p className="frosted-glass danger-frost remove-margin text-center py-2 px-4 mb-0">{errorMessage}</p>
					</>
				)}
				{!errorMessage && (
					<div className="flex flex-col pt-2 px-4">
						<button onClick={downloadImage} className={props.content.classes || "default-button w-100 local-margin mb-3"}>
							{props.content.buttons.save}
						</button>

						{props.content.buttons.next && (
							<button onClick={setUserImage} className={props.content.classes || "default-button w-100 local-margin"}>
								{props.content.buttons.next}
							</button>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

ImageEditor.propTypes = {
	userImg: PropTypes.object,
	showCamera: PropTypes.func,
	setUserImage: PropTypes.func,
	content: PropTypes.object
};

export default ImageEditor;
